import { useImportCsvMutation } from '@/features/imports/api/use-import-csv-mutation';
import { queryKeys } from '@/lib/query-keys';
import type {
  ImportCalendarCsvInput,
  ImportCalendarCsvResponse,
} from '@borradh-workspace/api-client/types';
import { importCalendarCsvResponseSchema } from '@borradh-workspace/contracts';

export const useImportCalendarCsv = (options?: {
  onSuccess?: (result: ImportCalendarCsvResponse) => void;
}) => {
  const { mutate, mutateAsync, isImporting } = useImportCsvMutation<
    ImportCalendarCsvInput,
    ImportCalendarCsvResponse
  >({
    path: 'appointments/import-csv',
    schema: importCalendarCsvResponseSchema,
    event: 'calendar_imported_csv',
    track: (r) => ({
      appointmentsCreated: r.appointmentsCreated,
      appointmentsUpdated: r.appointmentsUpdated,
      blockedTimesCreated: r.blockedTimesCreated,
      shiftsCreated: r.shiftsCreated,
      customersCreated: r.customersCreated,
      doubleBooked: r.doubleBooked,
      skipped: r.skipped,
      rowsInFile: r.rowsInFile,
    }),
    // Bookings and blocks render from two different query roots.
    invalidate: (r) => [
      queryKeys.appointments.all(),
      queryKeys.blockedTime.all(),
      ...(r.customersCreated > 0 ? [queryKeys.leads.all()] : []),
    ],
    summarize: (r) => {
      const parts: string[] = [];
      if (r.appointmentsCreated > 0)
        parts.push(`${r.appointmentsCreated} bookings`);
      if (r.appointmentsUpdated > 0)
        parts.push(`${r.appointmentsUpdated} bookings updated`);
      if (r.blockedTimesCreated > 0)
        parts.push(`${r.blockedTimesCreated} blocked times`);
      if (r.shiftsCreated > 0) parts.push(`${r.shiftsCreated} shifts`);
      if (r.customersCreated > 0)
        parts.push(`${r.customersCreated} new customers`);
      if (r.skipped > 0) parts.push(`${r.skipped} already there`);
      if (r.errors.length > 0)
        parts.push(`${r.errors.length} rows need attention`);
      return parts;
    },
    onSuccess: options?.onSuccess,
  });
  return {
    importCalendarCsv: mutate,
    importCalendarCsvAsync: mutateAsync,
    isImporting,
  };
};
